import { TextFieldProps, TextFieldVariants } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { forwardRef, Ref } from 'react';
import {
    ControllerRenderProps,
    FieldError,
    FieldValues,
    Path,
} from 'react-hook-form';

import { StyledTextareaAutoresize } from './styled-textarea-autoresize';

import { useUserPermissions } from '@/hooks/use-user-permissions';
import { useTelmaPortalStore } from '@/store/telma-portal-store';
import { displayErrorMessage } from '@/utils/display-error-message';

//TODO  Function components cannot be given refs. Attempts to access this ref will fail. Did you mean to use React.forwardRef()?

type CustomFormTextFieldProps<
    T extends FieldValues,
    Variant extends TextFieldVariants,
> = Omit<TextFieldProps, 'variant'> &
    ControllerRenderProps<T, Path<T>> & {
        fieldError?: FieldError;
        variant?: Variant;
        fullWidth?: boolean;
    } & {
        autoResize?: boolean;
    };

export const BotContentTextField = forwardRef<
    any,
    CustomFormTextFieldProps<FieldValues, TextFieldVariants>
>((props, ref) => {
    const { isBotEditor } = useUserPermissions();

    const language = useTelmaPortalStore((state) => state.editingBot.language);

    return (
        <FormTextField
            {...props}
            ref={ref}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            disabled={!isBotEditor || props.disabled}
        />
    );
});

BotContentTextField.displayName = 'BotContentTextField';

function createFormTextField<
    T extends FieldValues,
    Variant extends TextFieldVariants,
>() {
    const FormTextField = forwardRef<any, CustomFormTextFieldProps<T, Variant>>(
        ({ fieldError, autoResize, ...rest }, ref: Ref<any>) => {
            const helperText = displayErrorMessage(
                fieldError?.message,
                !!fieldError,
            );

            if (autoResize) {
                const { fullWidth, multiline, ...textareaProps } = rest;
                return (
                    <>
                        <StyledTextareaAutoresize
                            maxRows={5}
                            {...textareaProps}
                            value={rest.value ?? ''}
                            ref={ref}
                            fullWidth={fullWidth}
                            error={fieldError}
                        />
                        {helperText && (
                            <Box
                                sx={{
                                    color: 'rgb(244, 67, 54)',
                                    margin: '3px 14px 0px;',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {helperText}
                            </Box>
                        )}
                    </>
                );
            }
            return (
                <TextField
                    {...rest}
                    ref={ref}
                    value={rest.value ?? ''}
                    error={!!fieldError}
                    helperText={helperText}
                />
            );
        },
    );

    FormTextField.displayName = 'FormTextField';

    return FormTextField;
}

export const FormTextField = createFormTextField();
FormTextField.displayName = 'FormTextField';

function createFormTextFieldWithRef<
    T extends FieldValues,
    Variant extends TextFieldVariants,
>() {
    const FormTextFieldWithRef = forwardRef<
        any,
        CustomFormTextFieldProps<T, Variant>
    >(({ fieldError, ...rest }, ref: Ref<any>) => {
        const helperText = displayErrorMessage(
            fieldError?.message,
            !!fieldError,
        );

        return (
            <TextField
                {...rest}
                ref={ref}
                value={rest.value ?? ''}
                error={!!fieldError}
                helperText={helperText}
            />
        );
    });

    FormTextFieldWithRef.displayName = 'FormTextFieldWithRef';

    return FormTextFieldWithRef;
}

export const FormTextFieldWithRef = createFormTextFieldWithRef();

FormTextFieldWithRef.displayName = 'FormTextFieldWithRef';

//TODO  Warning: Function components cannot be given refs. Attempts to access this ref will fail. Did you mean to use React.forwardRef()?
//Check the render method of `Controller`.
